import LegalLayout from './LegalLayout';
import { TermsES } from './LegalSpanish';
import { useLanguage } from '../../i18n/LanguageContext';

const Terms = ({ user, setUser }) => {
  const { lang } = useLanguage();
  return (
    <LegalLayout
      user={user}
      setUser={setUser}
      titleKey="footer.linkTerms"
      titleFallback="Terms of Service"
      esTitle="Términos del Servicio"
      subtitleFallback="The rules of the road for using Match My Cleaner."
      esSubtitle="Las reglas del camino para usar Match My Cleaner."
      lastUpdated="February 17, 2026"
      testId="terms-of-service-page"
    >
      {lang === 'es' ? <TermsES /> : (
        <>
    <p>
      These Terms of Service ("Terms") form a binding agreement between you and Match My Cleaner ("we", "us", "our"). By
      registering, browsing, or transacting on the platform, you agree to these Terms in full. If you do not agree, do
      not use the Service.
    </p>

    <h2>1. The marketplace, not the service</h2>
    <p>
      Match My Cleaner is a <strong>marketplace</strong> that connects customers with independent cleaning professionals
      ("cleaners"). We are <strong>not</strong> a cleaning company. We do not employ cleaners, set their schedules,
      supervise their work, or guarantee the outcome of any service. Each cleaner is an independent contractor solely
      responsible for the services they provide.
    </p>

    <h2>2. Eligibility</h2>
    <p>You must be at least 18 years old and able to enter into a binding contract to use the Service.</p>

    <h2>3. Accounts</h2>
    <ul>
      <li>You're responsible for keeping your password confidential.</li>
      <li>You're responsible for all activity under your account.</li>
      <li>You agree to provide accurate, current, and complete information.</li>
      <li>We may suspend or terminate accounts that violate these Terms, with or without notice.</li>
    </ul>

    <h2>4. Customer responsibilities</h2>
    <ul>
      <li>Provide accurate service address, access instructions, and any special requirements at booking time.</li>
      <li>Be present (or arrange access) at the agreed time.</li>
      <li>Pay through the platform — never offer or accept off-platform payment to your cleaner.</li>
      <li>Treat cleaners with respect. Harassment, discrimination, or unsafe conditions are grounds for cancellation and account termination.</li>
    </ul>

    <h2>5. Cleaner responsibilities</h2>
    <p>By signing up as a cleaner, you separately agreed to the Cleaner Platform Agreement, which includes:</p>
    <ul>
      <li>Pass and maintain a clear background check (via Checkr) before accepting any bookings.</li>
      <li>Show up on time, prepared, and in professional condition.</li>
      <li>Never solicit, accept, or transact with platform-introduced customers outside Match My Cleaner.</li>
      <li>Honor cancellations, reschedules, and the platform fee structure.</li>
      <li>Each cleaner sets a <strong>supplies default</strong> at signup: either "I bring my own supplies" (cost folded into hourly rate) or "Customer provides supplies." Customers can override this at booking time — when a customer asks a "customer-provides" cleaner to bring supplies, a platform-set surcharge is added to the booking total and paid out to the cleaner.</li>
      <li>Carry your own equipment and (where applicable) liability insurance.</li>
    </ul>
    <p>
      Circumvention of the platform — including direct invoicing, off-platform payments, or sharing personal contact
      info to bypass the marketplace — is a material breach. We reserve the right to suspend, terminate, forfeit pending
      earnings, and pursue liquidated damages equal to the platform fees that would have been earned on bypassed transactions.
    </p>

    <h2>6. Payments, fees & payouts</h2>
    <ul>
      <li>Customer is charged the booking total at the time of booking confirmation, via Stripe.</li>
      <li>Match My Cleaner deducts a platform fee (currently 15% of the booking total; subject to change with notice). The remainder is paid to the cleaner.</li>
      <li>Pricing may include a surge multiplier during peak demand and a cleaner-tier multiplier (Premium 1.15× / Elite 1.25×) based on the cleaner's rating and review count.</li>
      <li>Cleaners are paid out on a rolling schedule after the service is completed and any dispute window has passed.</li>
      <li>Tips paid through the platform go 100% to the cleaner with no platform fee.</li>
    </ul>

    <h2>7. Cancellations, reschedules & no-shows</h2>
    <p>See the <a href="/refund-policy">Refund Policy</a> for full details.</p>
    <ul>
      <li><strong>≥ 24 hours before service</strong> — free cancellation, full refund.</li>
      <li><strong>&lt; 24 hours before service</strong> — late cancellation fee applies (currently 50% of booking total), split between cleaner and platform.</li>
      <li><strong>Reschedules</strong> follow the same window and may be blocked if they conflict with another booking.</li>
      <li><strong>Cleaner no-show</strong> — full refund + we'll help rebook with another cleaner.</li>
    </ul>

    <h2>8. Reviews & ratings</h2>
    <p>
      Only customers who completed a paid booking may submit a review. Reviews must be honest and based on first-hand
      experience. We may remove reviews that violate these standards (defamation, off-topic, personal attacks). We do not
      mediate factual disputes between users in the public review thread.
    </p>

    <h2>9. Prohibited conduct</h2>
    <ul>
      <li>Posting false, fraudulent, or misleading content.</li>
      <li>Using the Service to facilitate illegal activity.</li>
      <li>Bypassing the platform for paid services with users you met through Match My Cleaner.</li>
      <li>Reverse-engineering, scraping, or interfering with the platform's operation.</li>
      <li>Harassment, hate speech, threats, or unlawful discrimination.</li>
    </ul>

    <h2>10. Intellectual property</h2>
    <p>
      The Match My Cleaner name, logo, design, code, and content are owned by us or our licensors. You may not copy,
      modify, distribute, or create derivative works without our written permission.
    </p>

    <h2>11. Disclaimer of warranties</h2>
    <p>
      The Service is provided "as is" and "as available," without warranties of any kind, express or implied. We do not
      warrant that cleaners' services will meet your expectations, that the platform will be uninterrupted, or that defects
      will be corrected. Use of the Service is at your own risk.
    </p>

    <h2>12. Limitation of liability</h2>
    <p>
      To the maximum extent permitted by law, Match My Cleaner is not liable for any indirect, incidental, special,
      consequential, or punitive damages, or any loss of profits, data, or goodwill, arising from your use of the Service.
      Our aggregate liability for any claim is limited to the greater of (a) the platform fees you paid us in the 12 months
      before the claim, or (b) $100.
    </p>

    <h2>13. Indemnification</h2>
    <p>
      You agree to indemnify and hold harmless Match My Cleaner, its affiliates, and their respective officers, directors,
      and employees from any claims, damages, liabilities, costs, or expenses (including reasonable attorneys' fees)
      arising from your use of the Service, your breach of these Terms, or your violation of any third-party rights.
    </p>

    <h2>14. Dispute resolution & arbitration</h2>
    <p>
      Disputes between you and Match My Cleaner will be resolved by binding individual arbitration administered by the American
      Arbitration Association under its Consumer Arbitration Rules, except small-claims-court actions and intellectual-property
      claims. You waive the right to a jury trial and to participate in a class action.
    </p>

    <h2>15. Governing law</h2>
    <p>
      These Terms are governed by the laws of the State of Delaware, U.S.A., without regard to conflict-of-law principles.
    </p>

    <h2>16. Changes to these Terms</h2>
    <p>
      We may update these Terms at any time. Material changes will be posted on the platform with a new "Last updated"
      date and (when feasible) notified to you by email. Continued use after the effective date constitutes acceptance.
    </p>

    <h2>17. Contact</h2>
    <p>Questions about these Terms? Reach us via the Contact page.</p>
        </>
      )}
    </LegalLayout>
  );
};

export default Terms;
